/**
 * useWakeLock — mantém a tela do dispositivo sempre acesa enquanto a aba
 * está visível. Usa a Screen Wake Lock API nativa.
 *
 * Comportamento:
 * - Solicita o lock automaticamente na primeira interação do usuário
 *   (requisito da API — navegadores exigem gesto do usuário).
 * - Se a aba vai pra background → o navegador libera o lock automaticamente.
 * - Quando a aba volta → a gente re-adquire automaticamente.
 * - Fallback silencioso em navegadores sem suporte (não trava, só não funciona).
 *
 * Use uma única vez no App root.
 */
import { useEffect, useRef } from "react";

export function useWakeLock(enabled = true) {
  const lockRef = useRef(null);
  const enabledRef = useRef(enabled);

  useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  useEffect(() => {
    if (!enabled) return;

    // API não suportada? Sai silenciosamente.
    if (typeof navigator === "undefined" || !("wakeLock" in navigator)) {
      return;
    }

    let released = false;

    const acquire = async () => {
      if (!enabledRef.current) return;
      if (document.visibilityState !== "visible") return;
      if (lockRef.current) return; // já tem
      try {
        const lock = await navigator.wakeLock.request("screen");
        lockRef.current = lock;
        // Se o SO liberar (ex: bateria baixa), limpa a ref pra reacquisição
        lock.addEventListener?.("release", () => {
          lockRef.current = null;
        });
      } catch (e) {
        // Alguns navegadores rejeitam se a chamada não vier de gesto do usuário
        // — nesse caso, tentamos de novo quando o usuário clicar/tocar.
      }
    };

    const releaseLock = async () => {
      try { await lockRef.current?.release?.(); } catch (_e) { /* ignore */ }
      lockRef.current = null;
    };

    // Tenta ao montar (funciona se app já teve gesto do usuário)
    acquire();

    // Reacquisição quando volta pro foreground
    const onVisibility = () => {
      if (document.visibilityState === "visible") acquire();
    };
    document.addEventListener("visibilitychange", onVisibility);

    // Reacquisição em qualquer gesto do usuário (garante o first-request)
    const onUserGesture = () => acquire();
    document.addEventListener("click", onUserGesture, { passive: true });
    document.addEventListener("touchstart", onUserGesture, { passive: true });
    document.addEventListener("keydown", onUserGesture, { passive: true });

    return () => {
      released = true;
      document.removeEventListener("visibilitychange", onVisibility);
      document.removeEventListener("click", onUserGesture);
      document.removeEventListener("touchstart", onUserGesture);
      document.removeEventListener("keydown", onUserGesture);
      releaseLock();
      void released;
    };
  }, [enabled]);
}
